import React, { useState } from 'react';
import { Plus, X, ChevronDown, ChevronRight, Image, Bone } from 'lucide-react';
import { CascadeSymbolConfig, CascadeSymbolCategory } from '../../../../core/types/cascade-slot.types';
import { useProjectPanelStore } from '../../../store/projectPanelStore';
import { useAssetUrl } from '../../../hooks/useAssetUrl';
import { SymbolSpineConfig } from './SymbolSpineConfig';

interface SymbolListEditorProps {
  symbols: CascadeSymbolConfig[];
  onChange: (symbols: CascadeSymbolConfig[]) => void;
}

const CATEGORIES: CascadeSymbolCategory[] = ['high', 'normal', 'scatter', 'bonus', 'wild'];
const DISPLAY_MODES: Array<{ value: CascadeSymbolConfig['displayMode']; label: string }> = [
  { value: 'color', label: 'Color' },
  { value: 'sprite', label: 'Sprite' },
  { value: 'spine', label: 'Spine' },
];

const SymbolThumbnail: React.FC<{ assetPath: string; className?: string }> = ({ assetPath, className }) => {
  const { url, loading } = useAssetUrl(assetPath);
  if (loading) return <div className={`bg-zinc-700 animate-pulse ${className ?? ''}`} />;
  if (!url) return null;
  return <img src={url} alt="" className={`object-cover ${className ?? ''}`} />;
};

const TextureDropZone: React.FC<{
  textureAssetPath?: string;
  onAssign: (path: string) => void;
  onClear: () => void;
}> = ({ textureAssetPath, onAssign, onClear }) => {
  const [dragOver, setDragOver] = useState(false);
  const { url, loading } = useAssetUrl(textureAssetPath ?? null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);

    const assetId = e.dataTransfer.getData('text/plain');
    if (!assetId) return;

    const assets = useProjectPanelStore.getState().assets;
    const asset = assets[assetId];
    if (!asset) return;

    if (asset.type !== 'sprite') return;

    onAssign(asset.path);
  };

  if (textureAssetPath && url) {
    const fileName = textureAssetPath.split('/').pop() ?? textureAssetPath;
    return (
      <div className="flex items-center gap-1.5 bg-zinc-800 border border-zinc-700 rounded p-1">
        <img src={url} alt="" className="w-8 h-8 rounded-sm object-contain bg-zinc-900 flex-shrink-0" />
        <span className="text-[10px] text-zinc-300 flex-1 truncate">{fileName}</span>
        <button
          className="p-0.5 rounded hover:bg-zinc-600 text-zinc-500 hover:text-red-400 flex-shrink-0"
          onClick={onClear}
          title="Clear texture"
        >
          <X size={10} />
        </button>
      </div>
    );
  }

  return (
    <div
      className={`flex items-center justify-center gap-1 border-2 border-dashed rounded py-2 transition-colors ${
        dragOver ? 'border-blue-500 bg-blue-500/10' : 'border-zinc-600 hover:border-zinc-500'
      }`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {loading ? (
        <span className="text-[10px] text-zinc-500">Loading...</span>
      ) : (
        <>
          <Image size={12} className="text-zinc-500" />
          <span className="text-[10px] text-zinc-500">Drag image here</span>
        </>
      )}
    </div>
  );
};

export const SymbolListEditor: React.FC<SymbolListEditorProps> = ({
  symbols,
  onChange,
}) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const updateSymbol = (index: number, updates: Partial<CascadeSymbolConfig>) => {
    const updated = symbols.map((s, i) => (i === index ? { ...s, ...updates } : s));
    onChange(updated);
  };

  const addSymbol = () => {
    const nextId = `S${symbols.length + 1}`;
    const newSymbol: CascadeSymbolConfig = {
      id: nextId,
      name: `Symbol ${symbols.length + 1}`,
      category: 'normal',
      color: '#6B7280',
      textColor: '#FFFFFF',
      weight: 100,
      freeGameWeight: 100,
      paytable: [{ minCount: 8, maxCount: 9, payout: 10 }, { minCount: 10, maxCount: 11, payout: 20 }, { minCount: 12, maxCount: 30, payout: 50 }],
      displayMode: 'color',
    };
    onChange([...symbols, newSymbol]);
  };

  const removeSymbol = (index: number) => {
    onChange(symbols.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-1">
      {symbols.map((symbol, index) => (
        <div key={symbol.id} className="border border-zinc-700 rounded bg-zinc-800/50">
          <div
            className="flex items-center gap-1.5 px-1.5 py-1 cursor-pointer hover:bg-zinc-700/30"
            onClick={() => setExpandedId(expandedId === symbol.id ? null : symbol.id)}
          >
            {expandedId === symbol.id ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
            {symbol.displayMode === 'sprite' && symbol.textureAssetPath ? (
              <div className="w-5 h-5 rounded-sm flex-shrink-0 overflow-hidden bg-zinc-900">
                <SymbolThumbnail assetPath={symbol.textureAssetPath} className="w-5 h-5" />
              </div>
            ) : symbol.displayMode === 'spine' ? (
              <div className="w-5 h-5 rounded-sm flex items-center justify-center bg-violet-900/50 flex-shrink-0">
                <Bone size={12} className="text-violet-400" />
              </div>
            ) : (
              <div
                className="w-5 h-5 rounded-sm flex items-center justify-center text-[10px] font-bold flex-shrink-0"
                style={{ backgroundColor: symbol.color, color: symbol.textColor }}
              >
                {symbol.id}
              </div>
            )}
            <span className="text-xs text-zinc-300 flex-1 truncate">{symbol.name}</span>
            <span className="text-[10px] text-zinc-500">{symbol.category}</span>
            <span className="text-[10px] text-zinc-500">w:{symbol.weight}</span>
            <button
              className="p-0.5 rounded hover:bg-zinc-600 text-zinc-500 hover:text-red-400"
              onClick={(e) => { e.stopPropagation(); removeSymbol(index); }}
              title="Remove symbol"
            >
              <X size={10} />
            </button>
          </div>
          {expandedId === symbol.id && (
            <div className="px-2 pb-2 pt-1 space-y-1.5 border-t border-zinc-700">
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-zinc-500 w-12">Color</span>
                <input
                  type="color"
                  value={symbol.color}
                  onChange={(e) => updateSymbol(index, { color: e.target.value })}
                  className="w-6 h-5 bg-transparent border-0 cursor-pointer p-0"
                />
                <span className="text-[10px] text-zinc-400">{symbol.color}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-zinc-500 w-12">Display</span>
                <select
                  className="bg-zinc-800 border border-zinc-700 rounded px-1 py-0.5 text-xs text-zinc-300 flex-1"
                  value={symbol.displayMode ?? 'color'}
                  onChange={(e) => updateSymbol(index, { displayMode: e.target.value as CascadeSymbolConfig['displayMode'] })}
                >
                  {DISPLAY_MODES.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>
              {(symbol.displayMode ?? 'color') === 'sprite' && (
                <div className="space-y-1">
                  <span className="text-[10px] text-zinc-500">Texture</span>
                  <TextureDropZone
                    textureAssetPath={symbol.textureAssetPath}
                    onAssign={(path) => updateSymbol(index, { textureAssetPath: path })}
                    onClear={() => updateSymbol(index, { textureAssetPath: undefined })}
                  />
                </div>
              )}
              {(symbol.displayMode ?? 'color') === 'spine' && (
                <SymbolSpineConfig
                  spineConfig={symbol.spineConfig}
                  category={symbol.category}
                  onChange={(spineConfig) => updateSymbol(index, { spineConfig })}
                />
              )}
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-zinc-500 w-12">Name</span>
                <input
                  className="bg-zinc-800 border border-zinc-700 rounded px-1.5 py-0.5 text-xs text-zinc-300 flex-1"
                  value={symbol.name}
                  onChange={(e) => updateSymbol(index, { name: e.target.value })}
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-zinc-500 w-12">Category</span>
                <select
                  className="bg-zinc-800 border border-zinc-700 rounded px-1 py-0.5 text-xs text-zinc-300 flex-1"
                  value={symbol.category}
                  onChange={(e) => updateSymbol(index, { category: e.target.value as CascadeSymbolCategory })}
                >
                  {CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-zinc-500 w-12">Weight</span>
                <input
                  className="bg-zinc-800 border border-zinc-700 rounded px-1 py-0.5 text-xs text-zinc-300 w-16 text-center"
                  type="number"
                  value={symbol.weight}
                  min={1}
                  onChange={(e) => updateSymbol(index, { weight: parseInt(e.target.value) || 1 })}
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-zinc-500 w-12">FG Wt</span>
                <input
                  className="bg-zinc-800 border border-zinc-700 rounded px-1 py-0.5 text-xs text-zinc-300 w-16 text-center"
                  type="number"
                  value={symbol.freeGameWeight}
                  min={1}
                  onChange={(e) => updateSymbol(index, { freeGameWeight: parseInt(e.target.value) || 1 })}
                />
              </div>
              {symbol.paytable.length > 0 && (
                <div className="mt-1">
                  <span className="text-[10px] text-zinc-500 font-semibold">Paytable</span>
                  <div className="grid grid-cols-3 gap-1 text-[10px] text-zinc-500 mt-0.5">
                    <span>Min</span><span>Max</span><span>Payout</span>
                  </div>
                  {symbol.paytable.map((bracket, bIdx) => (
                    <div key={bIdx} className="grid grid-cols-3 gap-1 mt-0.5">
                      <input
                        className="bg-zinc-800 border border-zinc-700 rounded px-1 py-0.5 text-[10px] text-zinc-300 text-center"
                        type="number"
                        value={bracket.minCount}
                        min={1}
                        onChange={(e) => {
                          const newPaytable = symbol.paytable.map((b, j) =>
                            j === bIdx ? { ...b, minCount: parseInt(e.target.value) || 1 } : b
                          );
                          updateSymbol(index, { paytable: newPaytable });
                        }}
                      />
                      <input
                        className="bg-zinc-800 border border-zinc-700 rounded px-1 py-0.5 text-[10px] text-zinc-300 text-center"
                        type="number"
                        value={bracket.maxCount}
                        min={1}
                        onChange={(e) => {
                          const newPaytable = symbol.paytable.map((b, j) =>
                            j === bIdx ? { ...b, maxCount: parseInt(e.target.value) || 1 } : b
                          );
                          updateSymbol(index, { paytable: newPaytable });
                        }}
                      />
                      <input
                        className="bg-zinc-800 border border-zinc-700 rounded px-1 py-0.5 text-[10px] text-zinc-300 text-center"
                        type="number"
                        value={bracket.payout}
                        min={0}
                        onChange={(e) => {
                          const newPaytable = symbol.paytable.map((b, j) =>
                            j === bIdx ? { ...b, payout: parseInt(e.target.value) || 0 } : b
                          );
                          updateSymbol(index, { paytable: newPaytable });
                        }}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      ))}
      <button
        className="flex items-center gap-1 px-1 py-0.5 text-xs text-zinc-400 hover:text-zinc-200"
        onClick={addSymbol}
      >
        <Plus size={12} />
        Add Symbol
      </button>
    </div>
  );
};
